import { createMCPClient, type MCPClient } from '@ai-sdk/mcp';
import { type ToolSet } from 'ai';
import { createAuthorizationHeaderValue } from 'lib-shared-auth';

import { pickToolsByNamespace } from './toolSplit';

const DEFAULT_MAIN_PROMPT = 'chat-main-agent';
const DISCOVERY_API_PATH = 'v1/sidekick-config';
const MCP_PATH = 'v1/mcp';

type DiscoveredSubagent = {
  name: string;
  prompt: string;
  namespaces: string[];
};

type AgentDiscovery = {
  main: { prompt: string };
  subagents: DiscoveredSubagent[];
};

export type Subagent = {
  name: string;
  prompt: string;
  description: string;
  tools: ToolSet;
};

export type SubagentMcpClient = {
  mainPrompt: string;
  subagents: Subagent[];
  hasError: boolean;
  dispose: () => Promise<void>;
};

const authorisedFetch: typeof fetch = async (input, init) => {
  const authorization = await createAuthorizationHeaderValue();
  const headers = new Headers(init?.headers);
  headers.set('Authorization', authorization);
  return fetch(input, { ...init, headers });
};

const fetchAgentDiscovery = async (
  restUrl: string,
): Promise<AgentDiscovery | null> => {
  try {
    const response = await authorisedFetch(`${restUrl}/${DISCOVERY_API_PATH}`);
    if (!response.ok) return null;
    return (await response.json()) as AgentDiscovery;
  } catch {
    return null;
  }
};

const connect = async (url: string): Promise<MCPClient | null> => {
  try {
    return await createMCPClient({
      transport: { type: 'http', url, fetch: authorisedFetch },
    });
  } catch {
    return null;
  }
};

type DiscoveredTools = {
  tools: ToolSet;
  namespaceByToolName: Map<string, string>;
};

const EMPTY_DISCOVERED_TOOLS: DiscoveredTools = {
  tools: {},
  namespaceByToolName: new Map(),
};

const discover = async (client: MCPClient): Promise<DiscoveredTools> => {
  try {
    const definitions = await client.listTools();
    const namespaceByToolName = new Map<string, string>();
    for (const definition of definitions.tools) {
      const namespace = definition._meta?.namespace;
      if (typeof namespace === 'string')
        namespaceByToolName.set(definition.name, namespace);
    }
    return {
      tools: client.toolsFromDefinitions(definitions) as ToolSet,
      namespaceByToolName,
    };
  } catch {
    return EMPTY_DISCOVERED_TOOLS;
  }
};

const subagentDescription = (tools: ToolSet): string => {
  return Object.entries(tools)
    .map(([name, capability]) => {
      const description = capability.description?.trim();
      return description ? `${name}: ${description}` : name;
    })
    .join('; ');
};
/**
 * Using specialized orchestration
 * https://ai-sdk.dev/docs/agents/subagents#specialized-orchestration, this
 * function calls both the discovery API and the MCP Server. The discovery API
 * returns a configuration informing us how to provision the subagents.
 */
export const createSubagentMcpClient = async (
  restUrl: string,
): Promise<SubagentMcpClient> => {
  const [discovery, client] = await Promise.all([
    fetchAgentDiscovery(restUrl),
    connect(`${restUrl}/${MCP_PATH}`),
  ]);

  const { tools, namespaceByToolName } = client
    ? await discover(client)
    : EMPTY_DISCOVERED_TOOLS;

  const hasError = !discovery || !client;

  const dispose = async (): Promise<void> => {
    if (client) await client.close();
  };

  if (!discovery) {
    return {
      mainPrompt: DEFAULT_MAIN_PROMPT,
      subagents: [],
      hasError,
      dispose,
    };
  }

  const subagents: Subagent[] = discovery.subagents.map((subagent) => {
    /**
     * By using the namespace from the discovery API and the available tools
     * from the MCP Server, we have enough info to provision the subagent with
     * it's assigned set of tools.
     */
    const subagentTools = pickToolsByNamespace(
      tools,
      namespaceByToolName,
      subagent.namespaces,
    );
    return {
      name: subagent.name,
      prompt: subagent.prompt,
      description: subagentDescription(subagentTools),
      tools: subagentTools,
    };
  });

  return { mainPrompt: discovery.main.prompt, subagents, hasError, dispose };
};
